/**
 * 应用路由键与静态导出 href 的路径形态。
 *
 * Callers: navigation、RouteGuard、AppShell、useAppRouter、各 build*Href
 *
 * Modules:
 * - normalizeApplicationPathname — 去掉尾斜杠，得到稳定路由键
 * - toStaticExportHref — 补齐 `trailingSlash: true` 的目录斜杠后再拼 query
 * - toAppNavigationHref — 站内相对路径走 toStaticExportHref；绝对 URL 原样
 */

/** 归一化为无尾斜杠的应用路由键（根路径仍为 `/`）。 */
export function normalizeApplicationPathname(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, "");
  return normalized || "/";
}

/**
 * 静态导出 href：非根路径带尾斜杠，再拼接 query。
 * 路由键保持无斜杠；避免 `/campaigns` 与 `/campaigns/` 互相矫正导致软导航闪屏。
 *
 * @param pathname - 路径，或已含 query 的 href
 * @param search - 可选 query（`a=1` 或 `?a=1`）；传入时覆盖 pathname 上的 query
 */
export function toStaticExportHref(pathname: string, search?: string): string {
  const queryIndex = pathname.indexOf("?");
  const rawPath = queryIndex >= 0 ? pathname.slice(0, queryIndex) : pathname;
  const embeddedSearch = queryIndex >= 0 ? pathname.slice(queryIndex + 1) : "";
  const path = normalizeApplicationPathname(rawPath);
  const exported = path === "/" ? "/" : `${path}/`;
  const query = (search ?? embeddedSearch).replace(/^\?/, "");
  return query ? `${exported}?${query}` : exported;
}

/**
 * 浏览器导航用 href：`/` 开头的站内路径补目录斜杠；`https:` / `//` 等外链不改。
 *
 * @param href - router.push/replace 或 location.assign/replace 的目标
 */
export function toAppNavigationHref(href: string): string {
  if (!href.startsWith("/") || href.startsWith("//")) {
    return href;
  }
  return toStaticExportHref(href);
}
